import { useState, useContext } from "react";
import { Button } from "@/modern/components/ui/button";
import { RotateCcw, Loader2 } from "lucide-react";
import BryntumContext from "@/react/context/bryntumContext";
// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { revertToFactoryDefaults } from "@/network/network";
import { toast } from "sonner";
import { isIAmOneOfBoardOwner } from "@/other/utils/utils";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/modern/components/ui/dialog";
import { SETTINGS_STRUCTURE, Subcategory } from "../constants/settingsStructure";

const RevertToFactoryButton = () => {
  const { user, project, boardManager, selectedBoard } = useContext(BryntumContext);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isReverting, setIsReverting] = useState(false);

  // @ts-expect-error - userPreferences is dynamically set in bryntumContext
  const hasOpenAIKey = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";

  // Permission check: board owner only, excluding demo users
  // @ts-expect-error - boardManager is dynamically set in bryntumContext
  const boardStore = boardManager?.boardNameStore?.getById(selectedBoard?.id);
  const isBoardOwner = project && boardStore ? isIAmOneOfBoardOwner(project, boardStore.owner) : false;
  const canWrite = isBoardOwner && !user?.isDemoUser;
  const isDisabled = !canWrite || !hasOpenAIKey;

  // @ts-expect-error - contextCardsStore is added dynamically in projectConfig
  const contextCardsStore = project?.contextCardsStore;

  // Get AI Prompt subcategories from settings structure
  const aiPromptCategories: Subcategory[] = SETTINGS_STRUCTURE.aiPrompt.subcategories;

  const handleRevert = async () => {
    if (!canWrite) {
      toast.error("You don't have permission to revert context cards");
      return;
    }

    // @ts-expect-error - selectedBoard.record type not fully defined
    if (!contextCardsStore || !project || !selectedBoard?.record?.id) {
      toast.error("Unable to revert: context cards store not available");
      return;
    }

    setIsReverting(true);
    try {
      // Call backend to revert to factory defaults
      // @ts-expect-error - selectedBoard.record type not fully defined
      await revertToFactoryDefaults(selectedBoard.record.id);

      // Clear draft preference since we're back to defaults
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.aiContextCardInDraft, "false", true);

      toast.success("Successfully reverted to factory defaults");
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error reverting to factory defaults:", error);
      toast.error("Failed to revert to factory defaults. Please try again.");
    } finally {
      setIsReverting(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setIsDialogOpen(true)}
        variant="outline"
        size="sm"
        className="shadow-sm"
        disabled={isDisabled || isReverting}
        title={!hasOpenAIKey ? "OpenAI API key required" : !canWrite ? "Board Owner permission required" : "Revert all AI prompt settings to factory defaults"}
      >
        {isReverting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
        Revert To Factory
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Revert to Factory Defaults?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 px-6 py-4">
            <p className="text-base text-muted-foreground">This will replace all current AI prompt settings with factory defaults across all categories:</p>
            <div className="bg-muted/50 rounded-lg p-4 border border-border">
              <ul className="grid grid-cols-2 gap-2">
                {aiPromptCategories.map((subcategory) => (
                  <li key={subcategory.id} className="flex items-center gap-2 text-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                    <span className="text-foreground font-medium">{subcategory.label}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
              <p className="text-sm font-semibold text-destructive leading-relaxed">⚠️ All your custom changes will be lost. This action cannot be undone unless you publish before reverting.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isReverting} size="sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevert} disabled={isReverting} size="sm">
              {isReverting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Reverting...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Revert to Factory
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RevertToFactoryButton;
